export interface OAuthProvider {
  name: string;
  domain: string;
  defaultScopes: string[];
  defaultMandatoryScopes: string[];
  authUrl: string;
  tokenUrl?: string;
  userInfoUrl?: string;
  key?: string;
}

export const OAUTH_PROVIDERS: Record<string, OAuthProvider> = {
  google: {
    name: 'Google',
    domain: 'https://accounts.google.com',
    defaultScopes: ['https://www.googleapis.com/auth/gmail.send', 'https://www.googleapis.com/auth/gmail.readonly'],
    defaultMandatoryScopes: ['email', 'openid', 'profile'],
    authUrl: 'https://accounts.google.com/o/oauth2/auth',
    tokenUrl: 'https://oauth2.googleapis.com/token',
    userInfoUrl: 'https://www.googleapis.com/oauth2/v2/userinfo'
  },
  facebook: {
    name: 'Facebook',
    domain: 'https://www.facebook.com',
    defaultScopes: ['public_profile'],
    defaultMandatoryScopes: ['email', 'public_profile'],
    authUrl: 'https://www.facebook.com/v12.0/dialog/oauth',
    tokenUrl: 'https://graph.facebook.com/v12.0/oauth/access_token',
    userInfoUrl: 'https://graph.facebook.com/me'
  },
  github: {
    name: 'GitHub',
    domain: 'https://github.com',
    defaultScopes: [],
    defaultMandatoryScopes: ['user:email'],
    authUrl: 'https://github.com/login/oauth/authorize',
    tokenUrl: 'https://github.com/login/oauth/access_token',
    userInfoUrl: 'https://api.github.com/user'
  },
  microsoft: {
    name: 'Microsoft',
    domain: 'https://login.microsoftonline.com',
    defaultScopes: [],
    defaultMandatoryScopes: ['openid', 'profile', 'email'],
    authUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    tokenUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
    userInfoUrl: 'https://graph.microsoft.com/v1.0/me'
  },
  auth0: {
    name: 'Auth0',
    domain: 'https://YOUR_DOMAIN.auth0.com',
    defaultScopes: [],
    defaultMandatoryScopes: ['email', 'openid', 'profile'],
    authUrl: 'https://YOUR_DOMAIN.auth0.com/authorize',
    tokenUrl: 'https://YOUR_DOMAIN.auth0.com/oauth/token',
    userInfoUrl: 'https://YOUR_DOMAIN.auth0.com/userinfo'
  },
  others: {
    name: 'Others',
    domain: 'https://',
    defaultScopes: [],
    defaultMandatoryScopes: ['email', 'openid', 'profile'],
    authUrl: 'https://',
    tokenUrl: 'https://',
    userInfoUrl: 'https://'
  }
};

// Helper function to get provider by domain
export function getProviderByDomain(domain: string): OAuthProvider | null {
  for (const [key, provider] of Object.entries(OAUTH_PROVIDERS)) {
    if (domain.includes(provider.domain.replace('https://', ''))) {
      return { ...provider, key };
    }
  }
  return null;
}

// Helper function to get provider by key
export function getProviderByKey(key: string): OAuthProvider | null {
  return OAUTH_PROVIDERS[key] || null;
}

// Helper function to get all scopes for a provider (provider mandatory + provider defaults + custom)
export function getAllScopesForProvider(providerKey: string, customScopes: string[] = []): string[] {
  const provider = OAUTH_PROVIDERS[providerKey];
  if (!provider) return [...OAUTH_PROVIDERS.others.defaultMandatoryScopes, ...customScopes];
  
  const allScopes = [...provider.defaultMandatoryScopes, ...provider.defaultScopes, ...customScopes];
  return Array.from(new Set(allScopes)); // Remove duplicates
}

// Helper function to get provider options for dropdown
export function getProviderOptions() {
  return Object.entries(OAUTH_PROVIDERS).map(([key, provider]) => ({
    value: key,
    label: provider.name
  }));
}
